import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase, PROOF_PHOTOS_BUCKET } from "../../lib/supabase";
import { keys } from "../../lib/queryClient";
import { compressImage } from "../../lib/image";

/**
 * Replace your profile photo.
 *
 * Uploads under your own uid prefix in the proof-photos bucket, points the
 * profile at it, then removes the previous file. The old file goes last and
 * best-effort, so a failure can never leave the profile pointing at nothing.
 */
export function useUploadAvatar(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, previousPath }: { file: File; previousPath: string | null }) => {
      const blob = await compressImage(file);
      const path = `${userId}/avatar-${Date.now()}.jpg`;

      const upload = await supabase.storage
        .from(PROOF_PHOTOS_BUCKET)
        .upload(path, blob, { contentType: blob.type || "image/jpeg" });
      if (upload.error) throw upload.error;

      const { error } = await supabase.from("profiles").update({ avatar_path: path }).eq("id", userId);
      if (error) {
        await supabase.storage.from(PROOF_PHOTOS_BUCKET).remove([path]);
        throw error;
      }

      if (previousPath) {
        await supabase.storage.from(PROOF_PHOTOS_BUCKET).remove([previousPath]);
      }
      return path;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.profiles }),
  });
}
